import { EditorTree, TreeListItem } from '../../../types/types';
import testTreeData from './json_tree_test.json';

/**
 * Mock implementation of the Tree service for local development and testing
 */
class MockTreeService {
  private simulateDelay: () => Promise<void>;

  constructor(api_delay: number) {
    this.simulateDelay = () => {
      return new Promise((resolve) => {
        setTimeout(resolve, api_delay);
      });
    };
  }

  /**
   * Get list of all trees for the current user
   */
  async fetchTreeList(): Promise<TreeListItem[]> {
    await this.simulateDelay();
    
    // Return mock tree list data
    return [
      {
        id: 'tree-1',
        title: 'Investment Decision Tree',
        description: 'Analysis for investment options with multiple scenarios',
        updatedAt: new Date().toISOString(),
        filename: 'Investment_Decision_Tree.json',
        canDelete: true
      },
      {
        id: 'tree-2',
        title: 'Product Launch Strategy',
        description: 'Decision tree for new product launch timing and approach',
        updatedAt: new Date(Date.now() - 86400000).toISOString(), // 1 day ago
        filename: 'Product_Launch_Strategy.json',
        canDelete: true
      },
      {
        id: 'tree-3',
        title: 'Risk Assessment Model',
        description: 'Probability-based risk assessment for project outcomes',
        updatedAt: new Date(Date.now() - 86400000 * 7).toISOString(), // 1 week ago
        filename: 'Risk_Assessment_Model.json',
        canDelete: false
      }
    ];
  }

  /**
   * Get a specific tree by ID
   */
  async fetchTree(id: string): Promise<EditorTree> {
    await this.simulateDelay();
    
    // Return a mock tree based on the ID
    if (id === 'tree-1') {
      return {
        id: 'tree-1',
        title: 'Investment Decision Tree',
        filename: 'Investment_Decision_Tree.json',
        description: 'Analysis for investment options with multiple scenarios',
        createdAt: new Date(Date.now() - 86400000 * 30).toISOString(),
        updatedAt: new Date().toISOString(),
        total_expected_value: 125000,
        nodes: [
          {
            id: 'node-1',
            name: 'Investment Decision',
            type: 'decision',
            value: 125000,
            x: 400,
            y: 100,
            level: 0,
            children: ['node-2', 'node-3'],
            edge: {}
          },
          {
            id: 'node-2',
            name: 'Stock Market',
            type: 'chance',
            value: 150000,
            x: 200,
            y: 200,
            parent_id: 'node-1',
            level: 1,
            children: ['node-4', 'node-5'],
            edge: {}
          },
          {
            id: 'node-3',
            name: 'Real Estate',
            type: 'chance',
            value: 100000,
            x: 600,
            y: 200,
            parent_id: 'node-1',
            level: 1,
            children: ['node-6', 'node-7'],
            edge: {}
          },
          {
            id: 'node-4',
            name: 'Bull Market',
            type: 'terminal',
            value: 250000,
            x: 100,
            y: 300,
            parent_id: 'node-2',
            level: 2,
            children: [],
            edge: { probability: 0.6 }
          },
          {
            id: 'node-5',
            name: 'Bear Market',
            type: 'terminal',
            value: 0,
            x: 300,
            y: 300,
            parent_id: 'node-2',
            level: 2,
            children: [],
            edge: { probability: 0.4 }
          },
          {
            id: 'node-6',
            name: 'Property Value Increase',
            type: 'terminal',
            value: 150000,
            x: 500,
            y: 300,
            parent_id: 'node-3',
            level: 2,
            children: [],
            edge: { probability: 0.7 }
          },
          {
            id: 'node-7',
            name: 'Property Value Decrease',
            type: 'terminal',
            value: -25000,
            x: 700,
            y: 300,
            parent_id: 'node-3',
            level: 2,
            children: [],
            edge: { probability: 0.3 }
          }
        ]
      };
    }
    
    if (id === 'tree-2') {
      return {
        id: 'tree-2',
        filename: 'Product_Launch_Strategy.json',
        title: 'Product Launch Strategy',
        description: 'Decision tree for new product launch timing and approach',
        createdAt: new Date(Date.now() - 86400000 * 14).toISOString(),
        updatedAt: new Date(Date.now() - 86400000).toISOString(),
        total_expected_value: 850000,
        nodes: [
          {
            id: 'node-1',
            name: 'Launch Timing',
            type: 'decision',
            value: 850000,
            x: 400,
            y: 100,
            level: 0,
            children: ['node-2', 'node-3'],
            edge: {}
          },
          {
            id: 'node-2',
            name: 'Launch Now',
            type: 'chance',
            value: 850000,
            x: 200,
            y: 200,
            parent_id: 'node-1',
            level: 1,
            children: ['node-4', 'node-5', 'node-6'],
            edge: {}
          },
          {
            id: 'node-3',
            name: 'Delay Launch',
            type: 'chance',
            value: 600000,
            x: 600,
            y: 200,
            parent_id: 'node-1',
            level: 1,
            children: ['node-7', 'node-8', 'node-9'],
            edge: {}
          }
          // Additional nodes would be here, each with an edge property as needed
        ]
      };
    }

    if (id === 'tree-3') {
      // Ensure all nodes in testTreeData.nodes have an edge property
      const nodesWithEdge = (testTreeData as any).nodes ? (testTreeData as any).nodes.map((node: any) => ({
        ...node,
        edge: node.edge || (node.probability !== undefined ? { probability: node.probability } : {})
      })) : [];
      return {
        id: 'tree-3',
        filename: 'Risk_Assessment_Model.json',
        title: 'Risk Assessment Model',
        description: 'Probability-based risk assessment for project outcomes',
        createdAt: new Date(Date.now() - 86400000 * 7).toISOString(),
        updatedAt: new Date(Date.now() - 86400000 * 7).toISOString(),
        total_expected_value: (testTreeData as any).total_expected_value || 0,
        nodes: nodesWithEdge
      };
    }
    
    // Return empty tree if not found
    return {
      id: id,
      filename: 'New_Tree.json',
      title: 'New Tree',
      description: 'Empty decision tree',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      nodes: []
    };
  }

  /**
   * Create or save a tree
   */
  async saveTree(tree: EditorTree): Promise<EditorTree> {
    await this.simulateDelay();
    
    // Simulate saving - in a real implementation, this would send the tree to the backend
    const savedTree = {
      ...tree,
      updatedAt: new Date().toISOString()
    };
    
    console.log('Tree saved to backend:', savedTree.id);
    
    return savedTree;
  }

  /**
   * Delete a tree
   */
  async deleteTree(id: string): Promise<void> {
    await this.simulateDelay();
    
    console.log('Tree deleted from backend:', id);
  }
}

export default MockTreeService;
